import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { fromApiBoolean } from '@liquidweb-dev/club-sauce/types/boolean';
import { AssetDetails } from 'modules/queries/asset/useDetails';
import ChevronIcon from '@liquidweb-dev/undercurrent/icons/animated/Chevron';
import ScalingIcon from '@liquidweb-dev/undercurrent/icons/lucide/Scaling';
import Button from '@liquidweb-dev/undercurrent/Button';
import ListSubheader from '@liquidweb-dev/undercurrent/ListSubheader';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import Collapse from '@liquidweb-dev/undercurrent/Collapse';
import useAssetCancellationReasonList from 'modules/queries/asset/cancellation/useReasonList';
import LWForm, { type FormSubmitHandler } from 'components/LWForm';
import HookedTextField from 'components/Form/HookedTextField';
import HookedSubmit from 'components/Form/HookedSubmit';
import { type CancellationFormData } from './constants';

export interface CancelServiceProps {
	uniqId: string;
	formValues: CancellationFormData;
	assetDetails: AssetDetails | undefined;
	onFormValuesChange: (values: CancellationFormData) => void;
	onSubmit: FormSubmitHandler<CancellationFormData>;
}

const CancelService = ({
	uniqId,
	formValues,
	assetDetails,
	onFormValuesChange,
	onSubmit,
}: CancelServiceProps) => {
	const history = useHistory();
	const showResize =
		fromApiBoolean(assetDetails?.capabilities?.resize) &&
		fromApiBoolean(assetDetails?.active) &&
		assetDetails?.powerStatus === 'on';

	const { isLoading: isReasonsLoading, data: cancellationReasons } =
		useAssetCancellationReasonList();
	const [formErrors, setFormErrors] = useState({
		cancellation_reason: '',
	});
	const [isMoreToSayOpen, setMoreToSayOpen] = useState(false);

	const handleMoreToSayClick = () => {
		setMoreToSayOpen(!isMoreToSayOpen);
	};

	return (
		<LWForm
			values={formValues}
			onValuesChange={onFormValuesChange}
			errors={formErrors}
			onErrorsChange={setFormErrors}
			validators={{
				cancellation_reason: (value) => {
					if (value === '') return 'Please select a cancellation reason';
					return '';
				},
			}}
			onSubmit={onSubmit}
		>
			<Stack sx={{ gap: 200 }}>
				<Stack>
					<Typography variant="body-md">
						We hate to see you go, but would love to know the reason you want to
						leave.
					</Typography>
				</Stack>
				<Stack direction="column" useFlexGap sx={{ width: '100%', gap: 300 }}>
					<Stack direction="column" useFlexGap sx={{ gap: 150 }}>
						<HookedTextField
							name="cancellation_reason"
							label="Reason for cancellation"
							size="medium"
							fullWidth
							select
							disabled={isReasonsLoading}
							slotProps={{
								select: {
									displayEmpty: true,
								},
							}}
						>
							<MenuItem value="" disabled sx={{ display: 'none' }}>
								Select a reason
							</MenuItem>
							{cancellationReasons?.map((data) => {
								if (data.type === 'category') {
									return (
										<ListSubheader
											sx={{ backgroundColor: 'transparent' }}
											key={data.label}
										>
											<Typography variant="label-xl" sx={{ fontWeight: 500 }}>
												{data.label}
											</Typography>
										</ListSubheader>
									);
								}

								return (
									<MenuItem key={data.label} value={data.label}>
										<Typography
											variant="label-xl"
											sx={{
												fontWeight: 400,
												whiteSpace: 'normal',
												lineHeight: '24px',
											}}
										>
											{data.label}
										</Typography>
									</MenuItem>
								);
							})}
						</HookedTextField>
					</Stack>
					{showResize && (
						<Stack
							sx={({ vars, borderRadius }) => ({
								border: `1px solid ${vars.palette.uc.highlight.border.medium}`,
								borderRadius: borderRadius.rounded,
								padding: 200,
								gap: 100,
							})}
						>
							<Stack
								direction="row"
								useFlexGap
								sx={{ gap: 100, alignItems: 'center' }}
							>
								<ScalingIcon
									sx={({ vars, size }) => ({
										color: vars.palette.uc.highlight.icon.main,
										width: size.sm,
										height: size.sm,
										flexShrink: 0,
									})}
								/>
								<Typography variant="body-md" sx={{ fontWeight: 500 }}>
									Scale down, not out
								</Typography>
							</Stack>
							<Stack sx={{ gap: 200 }}>
								<Typography variant="body-sm">
									Resize your server to cut down on cost instead of getting rid
									of it
								</Typography>
								<Button
									color="highlight"
									variant="secondary"
									size="medium"
									fullWidth
									onClick={() => history.push(`/servers/${uniqId}/resize`)}
								>
									Resize
								</Button>
							</Stack>
						</Stack>
					)}
					<Stack>
						<Stack
							direction="row"
							onClick={handleMoreToSayClick}
							sx={{
								justifyContent: 'space-between',
								cursor: 'pointer',
							}}
						>
							<Typography
								variant="label-lg"
								sx={({ vars }) => ({
									color: vars.palette.uc.text.weak,
									fontWeight: 500,
								})}
							>
								Have more to say?
							</Typography>
							<ChevronIcon
								open={isMoreToSayOpen}
								sx={{
									height: ({ size }) => size.xS,
									width: ({ size }) => size.xS,
								}}
							/>
						</Stack>
						<Collapse in={isMoreToSayOpen}>
							<HookedTextField
								name="additional_info"
								multiline
								placeholder="Anything else you'd like to let us know?"
								minRows={4}
								size="medium"
								fullWidth
								sx={{ marginTop: 200 }}
							/>
						</Collapse>
					</Stack>
				</Stack>
				<HookedSubmit size="large" variant="primary" fullWidth>
					Next
				</HookedSubmit>
			</Stack>
		</LWForm>
	);
};

export default CancelService;
